<template>
  <zm-dialog
    class="opc-zod"
    title=""
    :visible.sync="visible_"
    width="30%"
    append-to-body
    show-close
    @closed="afterClose"
    :class="{
      'opc-modal': $context.is.modal,
    }"
  >
    <div class="opc-zod__head">
      <h2>{{ $t('billing.delta.cur_plan_details') }}</h2>
      <span>{{ plansCount }} {{ $t('billing.order_summary.item_ss') }}</span>
    </div>
    <PlanDetail ref="planDetail" :aware="planDetailAware" />
    <div class="opc-zod__footer">
      <current-charge :modifier="currentChargeAmountModifier" />
    </div>
  </zm-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import Cart, {categories} from '../cart.vue';
import CurrentCharge from '@/views/config/shopping-cart/current.vue';
import { plansof } from '@/utils/chain';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import { CART_DISPLAY_MODE } from '@/plans/mx/shopping-cart.vue';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { isEmptyVal } from '@/utils';
import { dateTimeFormat } from '@/utils/dateformat';
import { calculateDiscountWithDiscountType } from '@/views/config/coupon/helper/utils';
import _ from 'lodash';

const PlanDetail = {
  props: {
    aware: {
      type: Function,
      default: () => {},
    },
  },
  mixins: [Cart],
  computed: {
    ...mapGetters(['boughtPlans', 'discountWithAmountMap']),
    mimic() {
      let boughtPlansOfType = plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).get();
      let mode = CART_DISPLAY_MODE.BOUGHT;
      const plans = [];
      boughtPlansOfType.forEach((plan) => {
        if (!plan.ratePlans.length) {
          plans.push(plan);
        } else {
          // TODO this is on condition that it is single plan with ratePlans
          mode = CART_DISPLAY_MODE.SUM;
          plan.ratePlans.forEach((ratePlan) => {
            // lets mimic rate plans to be plain ones
            plans.push({
              ...plan,
              amount: ratePlan.price,
              cartInfo: {
                selected_number: ratePlan.quantity,
              },
              ratePlans: [],
              plan_code: 'mimic_' + plan.plan_code,
            });
          });
        }
      });
      const data = {
        mode,
        plans,
      };
      this.aware(data);
      return data;
    },
    mode() {
      return this.mimic.mode;
    },
  },
  methods: {
    compute_delta_discount(plan) {
      const basicInfo = { discount: 0, expireDate: '' };
      if (isEmptyVal(plan)) return basicInfo;

      const { discountPlans } = this.discountWithAmountMap;
      const planCode = plan?.plan_code;
      const discountPlanInfo = discountPlans?.[planCode];
      if (isEmptyVal(discountPlanInfo)) return basicInfo;

      const boughtQuantity = plan?.cartInfo?.selected_number;
      const expireDate = discountPlanInfo?.expireDate ?? "";
      const discount = calculateDiscountWithDiscountType({
        discountType: discountPlanInfo?.couponDiscountType,
        discount: discountPlanInfo?.discount,
        amount: plan?.amount * boughtQuantity,
        quantity: boughtQuantity
      });

      return {
        discount,
        expireDate,
      };
    },
    mx_delta_discount_view(plan) {
      const { expireDate, discount } = this.compute_delta_discount(plan);
      if (!discount) return null;

      const expiresDesc = expireDate ?
        ` (${this.$t('billing.coupon.expiresDesc', [dateTimeFormat(expireDate)])})` : '';
      const cartDiscountLabel = `${this.$t('billing.coupon.discount')} ${expiresDesc}`;

      return (
        <div class="opc-cart-item__discount __PLACEHOLDER_discount_title__">
          <span>{cartDiscountLabel}</span>
          <span class="opc-cart-item__value text-no-capitalize">{this.$money(discount)}</span>
        </div>
      );
    },
    renderTitle(plan) {
      return (
        <div>
          {this.mx_title(
            this.$t(categories[plan.category] || plan.planNameKey),
            this.$money(this.mx_price$(plan)),
            plan?.plan_code,
            undefined,
            plan.category === ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP ? ['text-no-capitalize'] : null
          )}
        </div>
      );
    },
    mx_license(num, unit = this.$t('billing.planform.unit.user')) {
      if (this.$context.is.modalDelta) {
        unit = this.$t('billing.planform.unit.license')
      }

      return (
        <div class="opc-cart-item__text">
          {num} {unit}
        </div>
      );
    },
    $renderPrice(plans) {
      return (plans || this.mx_plans).map((plan) => {
        return (
          <div class="opc-cart-item">
            {this.renderTitle(plan, this.titleIncluded(plan))}
            {this.mx_delta_discount_view(plan)}
            {this.mx_license$(plan)}
            {this.mx_expire_date(this.expiredDate(plan)) || this.mx_cycle(plan.billing_cycle)}
          </div>
        );
      });
    }
  },
  render() {
    return <div>{this.$renderPrice(this.mimic.plans)}</div>;
  },
};

export default {
  data() {
    return {
      resolve_: null,
      visible_: false,
      plansCount: 1,
    };
  },
  computed: {
    ...mapGetters(['boughtPlans', 'getSelectedPlans', 'getSelectedPlansAmount']),
    currentPlansAmountInfo() {
      return {
        [BILLING_CYCLE.MONTHLY]: this.getSelectedPlansAmount?.monthlyAmount,
        [BILLING_CYCLE.QUARTERLY]: this.getSelectedPlansAmount?.quarterlyAmount,
        [BILLING_CYCLE.ANNUAL]: this.getSelectedPlansAmount?.annualAmount,
        [BILLING_CYCLE.TWO_YEARS]: this.getSelectedPlansAmount?.twoYearsAmount,
        [BILLING_CYCLE.THREE_YEARS]: this.getSelectedPlansAmount?.threeYearsAmount,
      }
    }
  },
  components: {
    PlanDetail,
    CurrentCharge,
  },
  methods: {
    planDetailAware({ plans }) {
      this.plansCount = plans.length;
    },
    $popup() {
      this.visible_ = true;
      this.$tracking.buyInteraction('product details', `${this.$attrs.trackingName} current plan details - impression`);
      return new Promise((resolve) => {
        this.resolve_ = resolve;
      });
    },
    afterClose() {
      this.$tracking.buyInteraction('product details', `${this.$attrs.trackingName} plan details - exit`);
    },
    onCancel() {
      this.visible_ = false;
      this.resolve_(false);
    },
    async onConfirm() {
      this.visible_ = false;
      this.resolve_();
    },
    currentChargeAmountModifier(list) {
      // HACK CurrentCharge > nextBill.amount > getSelectedPlans, we have to exclude the delta part
      const cartPlan = plansof(this.getSelectedPlans).type(planTypes.ZOOM_MEETING).get(0); // this is edit flow
      return list.map((x) => {
        if (x.billingCycle === cartPlan.billing_cycle) {
          // current shopping cart plan total amount with billing cycle
          const amount = _.get(this.currentPlansAmountInfo, [x.billingCycle], x.amount);
          const deltaAmount = this.$refs.planDetail.mx_price_delta(cartPlan);
          const boughtPlan = _.find(this.boughtPlans, (b) => b.plan_code === cartPlan.plan_code);
          const boughtPlanDiscount = this.$refs.planDetail.compute_delta_discount(boughtPlan)?.discount;
          // bought plans total amount - bought plans total discount
          x.amount = amount - deltaAmount - boughtPlanDiscount;
        }
        return x;
      });
    },
  },
};
</script>

<style lang="scss">
.opc-zod {
  font-family: Almaden Sans;
  &.opc-modal {
    .zm-dialog {
      margin: 0 !important;
      width: 85% !important;
      min-width: auto !important;
      height: auto;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  &__head {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #dfdfe4;
    padding-bottom: 8px;
    margin-bottom: 12px;
    h2 {
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
      text-align: left;
      color: #131619;
    }
    span {
      font-size: 14px;
      font-weight: 500;
      line-height: 32px;
      text-align: right;
      color: #6e7680;
    }
  }
  .opc-cart-item {
    margin-bottom: 12px;
  }
  &__footer {
    padding-top: 12px;
    border-top: 1px solid #dfdfe4;
  }
}
</style>
